import { api } from "./client";

export type ExaminationType =
  | "initial"
  | "periodic"
  | "form_086"
  | "form_083"
  | "form_089";

export type ExaminationStatus =
  | "open"
  | "in_progress"
  | "pending_chief"
  | "completed"
  | "cancelled";

export type FinalVerdict = "healthy" | "restricted_fit" | "unfit_for_work";
export type SectionVerdict = "healthy" | "unhealthy";
export type PaymentStatus = "unpaid" | "partial" | "paid";

export type DoctorSpecialty =
  | "therapist"
  | "neurologist"
  | "ophthalmologist"
  | "ent"
  | "surgeon"
  | "gynecologist"
  | "urologist"
  | "dentist"
  | "psychiatrist_narcologist"
  | "dermatovenerologist"
  | "cardiologist"
  | "radiologist"
  | "laboratory";

export interface IntakeVitals {
  height_cm?: number | null;
  weight_kg?: number | null;
  systolic_bp?: number | null;
  diastolic_bp?: number | null;
  pulse_bpm?: number | null;
  temperature_c?: number | null;
}

export interface IntakeAnamnesis {
  chronic_conditions: string[];
  allergies: { substance?: string; reaction?: string }[];
  past_surgeries: { year?: number; description?: string }[];
  current_medications: { name?: string; dose?: string }[];
  vaccinations: { vaccine?: string; date?: string }[];
  smoking?: string | null;
  alcohol?: string | null;
  family_history?: string | null;
}

export interface SectionRead {
  id: string;
  examination_id: string;
  specialty: DoctorSpecialty;
  assigned_user_id: string | null;
  assigned_user_name: string | null;
  completed_user_id: string | null;
  completed_user_name: string | null;
  diagnosis_code: string | null;
  verdict: SectionVerdict | null;
  completed_at: string | null;
  locked: boolean;
  // Detail fields populated by the backend only when the requesting user
  // is allowed to see the section's full content (chief / commission /
  // admin / registrar — or the assigned specialist themselves).
  complaints?: string | null;
  objective_findings?: string | null;
  diagnosis_text?: string | null;
  recommendations?: string | null;
  extra_fields?: Record<string, unknown> | null;
  lab_template_table?: {
    name: string;
    headers: string[];
    rows: string[][];
  } | null;
}

export interface SectionDetail extends SectionRead {
  complaints: string | null;
  objective_findings: string | null;
  diagnosis_text: string | null;
  recommendations: string | null;
  extra_fields: Record<string, unknown> | null;
  signature_object_key: string | null;
}

export interface ExaminationRead {
  id: string;
  clinic_id: string;
  patient_id: string;
  type: ExaminationType;
  ticket_number: string | null;
  exam_number: number;
  job_title: string | null;
  status: ExaminationStatus;
  payment_status: PaymentStatus;
  total_price: number | null;
  currency: string;
  organization_id: string | null;
  paid_amount: number | null;
  paid_at: string | null;
  payment_method: string | null;
  final_verdict: FinalVerdict | null;
  valid_until: string | null;
  created_by_user_id: string;
  created_at: string;
}

export interface ExaminationDetail extends ExaminationRead {
  intake_vitals: IntakeVitals | null;
  intake_anamnesis: IntakeAnamnesis | null;
  final_verdict_notes: string | null;
  chief_signed_user_id: string | null;
  chief_signed_at: string | null;
  sections: SectionRead[];
  verdict_diagnosis_summary: string | null;
  verdict_profession_fitness: string | null;
  verdict_pregnancy_relevant: boolean | null;
  verdict_referral_kind: string | null;
  verdict_referral_notes: string | null;
  verdict_final_decision: string | null;
}

export interface ExaminationList {
  items: ExaminationRead[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExaminationCreatePayload {
  patient_id: string;
  type: ExaminationType;
  job_title?: string | null;
  routing?: DoctorSpecialty[] | null;
  ticket_number?: string | null;
  total_price?: number | null;
  payment_status?: PaymentStatus;
  organization_id?: string | null;
}

export interface PaymentMarkPayload {
  amount?: number | null;
  method?: string | null;
  notes?: string | null;
  full?: boolean;
}

export async function markPayment(
  examId: string,
  payload: PaymentMarkPayload,
): Promise<ExaminationDetail> {
  const { data } = await api.post<ExaminationDetail>(
    `/examinations/${examId}/payment`,
    payload,
  );
  return data;
}

export async function cancelExamination(examId: string, reason: string): Promise<void> {
  await api.post(`/examinations/${examId}/cancel`, { reason });
}

export interface ProfpatologQueue {
  ready: ExaminationRead[];
  completed: ExaminationRead[];
}

export async function getProfpatologQueue(): Promise<ProfpatologQueue> {
  const { data } = await api.get<ProfpatologQueue>("/examinations/profpatolog-queue");
  return data;
}

export async function bulkDeleteExaminations(ids: string[]): Promise<{ deleted: number }> {
  const { data } = await api.post<{ deleted: number }>("/examinations/bulk-delete", { ids });
  return data;
}

export async function addSectionToExam(
  examId: string,
  specialty: DoctorSpecialty,
): Promise<void> {
  await api.post(`/examinations/${examId}/sections/${specialty}/add`);
}

export async function bulkDownloadFormsBlob(
  ids: string[],
  template: ExaminationType,
  lang: string = "uz",
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.post<Blob>(
    "/examinations/forms-zip",
    { ids, template, lang },
    { responseType: "blob" },
  );
  const cd = (response.headers["content-disposition"] as string) || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || (ids.length > 1 ? "koriklar.zip" : "korik.pdf");
  return { blob: response.data as Blob, filename };
}

export interface ExaminationUpdatePayload {
  intake_vitals?: IntakeVitals;
  intake_anamnesis?: IntakeAnamnesis;
  ticket_number?: string;
  total_price?: number;
  payment_status?: PaymentStatus;
}

export interface SectionUpdatePayload {
  complaints?: string;
  objective_findings?: string;
  diagnosis_code?: string;
  diagnosis_text?: string;
  recommendations?: string;
  verdict?: SectionVerdict;
  extra_fields?: Record<string, unknown>;
}

export interface WorklistItem {
  section_id: string;
  examination_id: string;
  patient_id: string;
  patient_card_number: string;
  patient_name: string;
  patient_age: number;
  examination_type: ExaminationType;
  completed_at: string | null;
  created_at: string;
}

export async function listExaminations(params: {
  status?: ExaminationStatus;
  type?: ExaminationType;
  patient_id?: string;
  assigned_doctor?: string;
  limit?: number;
  offset?: number;
}): Promise<ExaminationList> {
  const { data } = await api.get<ExaminationList>("/examinations", { params });
  return data;
}

export async function createExamination(
  payload: ExaminationCreatePayload,
): Promise<ExaminationDetail> {
  const { data } = await api.post<ExaminationDetail>("/examinations", payload);
  return data;
}

export async function getExamination(id: string): Promise<ExaminationDetail> {
  const { data } = await api.get<ExaminationDetail>(`/examinations/${id}`);
  return data;
}

export async function updateExamination(
  id: string,
  payload: ExaminationUpdatePayload,
): Promise<ExaminationDetail> {
  const { data } = await api.patch<ExaminationDetail>(`/examinations/${id}`, payload);
  return data;
}

export async function getSection(
  examId: string,
  specialty: DoctorSpecialty,
): Promise<SectionDetail> {
  const { data } = await api.get<SectionDetail>(
    `/examinations/${examId}/sections/${specialty}`,
  );
  return data;
}

export async function upsertSection(
  examId: string,
  specialty: DoctorSpecialty,
  payload: SectionUpdatePayload,
): Promise<SectionDetail> {
  const { data } = await api.put<SectionDetail>(
    `/examinations/${examId}/sections/${specialty}`,
    payload,
  );
  return data;
}

export async function getWorklist(only_open = true): Promise<{ items: WorklistItem[] }> {
  const { data } = await api.get<{ items: WorklistItem[] }>("/worklist", {
    params: { only_open },
  });
  return data;
}
